import { Text, View } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { decimalSeparator, thousandSeparator } from "../../../i18n";
import KiloNewton from "../../Math/Units/Metric/KiloNewton";
import { DataRow, DataRowTextValue } from "../DataRow";
import InvoiceTableBlankSpace from "../InvoiceTableBlankSpace";
import { PageWrapper, styles, tableStyles } from "../SmartTitanProjektPDF";

export function GermanyInnereErmuedung({ data }) {
  const { t } = useTranslation("translation");
  let itemIndex = 1;
  return (
    <PageWrapper Deckblatt={data.Deckblatt}>
      <View>
        {/* Pfahlkopf / Übergangsrohr */}
        <Text style={styles.section}>{t("inner.fatigue.checkboxText_pdf")}</Text>
        {/* Mindestabmessungen und Position des Übergangsrohrs */}
        <Text style={styles.sectionSubText}>{t("inner.fatigue.characteristicEffects")}</Text>

        <View style={tableStyles.tableContainer}>
          {/* Schwingungs-Obergrenze */}
          <DataRow
            item={{
              title: t("inner.fatigue.upperLimit"),
              value: data.Germany.Innere.Ermuedung.schwingungsObergrenze,
              // F_{k,O}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}F_{k,O}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}F_{k,O}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}F_{k,O}",
                height: 9,
                width: 16,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 2,
              itemIndex: itemIndex++,
              valueUnit: <KiloNewton />,
            }}
          />
          {/* Schwingungs-Untergrenze */}
          <DataRow
            item={{
              title: t("inner.fatigue.lowerLimit"),
              value: data.Germany.Innere.Ermuedung.schwingungsUntergrenze,
              // F_{k,O}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}F_{k,U}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}F_{k,U}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}F_{k,U}",
                height: 9,
                width: 16,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 2,
              itemIndex: itemIndex++,
              valueUnit: <KiloNewton />,
            }}
          />
          {/* Teilsicherheitsbeiwert (nach DIN EN 1991) */}
          <DataRow
            item={{
              title: t("inner.fatigue.partialSafetyFactor"),
              value: data.Germany.Innere.Ermuedung.teilsicherheitsbeiwertDinEN,
              // \gamma_{ _{Ff}}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}\gamma_{&space;_{Ff}}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\gamma_{&space;_{Ff}}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\gamma_{&space;_{Ff}}",
                height: 7,
                width: 11,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 2,
              itemIndex: itemIndex++,
            }}
          />
          {/* Bemessungswert der Schwingungsgrenze */}
          <DataRow
            item={{
              title: t("inner.fatigue.oscillationLimit"),
              value: data.Germany.Innere.Ermuedung.bemessungswertSchwingungsgrenze,

              // F_{d,\Delta\sigma}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}F_{d,\Delta\sigma}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}F_{d,\\Delta\\sigma}

              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}F_{d,\\Delta\\sigma}",
                height: 9,
                width: 20,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <KiloNewton />,

              // F_{d,\Delta\sigma}=\frac{F_{k,O}-F_{k,U}}{\gamma_{ _{Ff}}}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}F_{d,\Delta\sigma}=\frac{F_{k,O}-F_{k,U}}{\gamma_{&space;_{Ff}}}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}F_{d,\\Delta\\sigma}=\frac{F_{k,O}-F_{k,U}}{\\gamma_{&space;_{Ff}}}

              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}F_{d,\\Delta\\sigma}=\\frac{F_{k,O}-F_{k,U}}{\\gamma_{&space;_{Ff}}}",
                height: 15,
                width: 65,
              },
            }}
          />
          {/* Anzahl der Belastungszyklen */}
          <DataRow
            item={{
              title: t("inner.fatigue.NumberLoadCycles"),
              value: data.Germany.Innere.Ermuedung.anzahlBelastungszyklen,

              // N_{R}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}N_{R}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}N_{R}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}N_{R}",
                height: 7,
                width: 11,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 0,
              itemIndex: itemIndex++,
            }}
          />
        </View>

        {/* Nachweis */}
        <Text style={styles.sectionSubText}>{t("inner.fatigue.verification_pdf")}</Text>

        <View style={tableStyles.tableContainer}>
          {/* Wahl des Stahltraggliedes */}
          <DataRowTextValue
            item={{
              title: t("aeussre.LoadCarryingElement"),
              value: data.Germany.Innere.NachweisStahltragglied.wahlDesStahltraggliedes,

              itemIndex: itemIndex++,
            }}
          />
          {/* charakteristische Spannungsschwingbreite */}
          <DataRow
            item={{
              title: t("inner.fatigue.characteristicStressRange"),
              value: data.Germany.Innere.Ermuedung.charakteristischeSpannungsschwingbreite,

              // \sigma{ _{R,K}}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}\sigma{&space;_{R,K}}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\sigma{&space;_{R,K}}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}&space;\\Delta\\sigma_{\\tiny_{R,K}}",
                height: 9,
                width: 22,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 0,
              itemIndex: itemIndex++,
              //https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\textbf{N/mm}^4
              unitImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{100}\\bg{white}\\textbf{N/mm}^2",
                height: 9,
                width: 29,
              },
            }}
          />

          {/* Querschnitt */}
          <DataRow
            item={{
              title: t("inner.fatigue.crossSection"),
              value: data.Germany.Innere.Ermuedung.querschnitt,

              // A_s
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}A_s
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}A_s
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}A_s",
                height: 7,
                width: 9,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: "mm²",
            }}
          />

          {/* charakteristische Schwingbreite */}
          <DataRow
            item={{
              title: t("inner.fatigue.characteristicAmplitude"),
              value: data.Germany.Innere.Ermuedung.charakteristischeSchwingbreite,

              // R_{k, \Delta\sigma}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}R_{k,&space;\Delta\sigma}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_{k,&space;\\Delta\\sigma}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_{k,&space;\\Delta\\sigma}",
                height: 9,
                width: 21,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <KiloNewton />,

              // R_{k,\Delta\sigma}=\frac{\Delta\sigma_{ _{R,k}}}{A_s}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}R_{k,\Delta\sigma}=\frac{\Delta\sigma_{&space;_{R,k}}}{A_s}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_{k,\\Delta\\sigma}=\\frac{\\Delta\\sigma_{&space;_{R,k}}}{A_s}

              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_{k,\\Delta\\sigma}=\\frac{\\Delta\\sigma_{&space;_{R,k}}}{A_s}",
                height: 14,
                width: 52,
              },
            }}
          />

          {/* Teilsicherheitsbeiwert
              (Ermüdungsfestigkeit nach EN 1993-1-9; Tab. 3.1) */}
          <DataRow
            item={{
              title: t("inner.fatigue.partialSafetyFactor_2"),
              value: data.Germany.Innere.Ermuedung.teilsicherheitsbeiwertErmuedung,

              // \gamma_{ _{Mf}}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}\gamma_{&space;_{Mf}}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\gamma_{&space;_{Mf}}

              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\gamma_{&space;_{Mf}}",
                height: 7,
                width: 13,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 2,
              itemIndex: itemIndex++,
              valueUnit: <KiloNewton />,
            }}
          />

          {/* Bemessungswert der Schwingbreite */}
          <DataRow
            item={{
              title: t("inner.fatigue.oscillationLimit"),
              value: data.Germany.Innere.Ermuedung.bemessungswertSchwingbreite,

              // R_{d,\Delta\sigma}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}R_{d,\Delta\sigma}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_{d,\\Delta\\sigma}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_{d,\\Delta\\sigma}",
                height: 9,
                width: 21,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 1,
              itemIndex: itemIndex++,
              valueUnit: <KiloNewton />,

              // R_{d,\Delta\sigma}=\frac{R_{d,\Delta\sigma}}{\gamma_{ _Mf}}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}R_{d,\Delta\sigma}=\frac{R_{d,\Delta\sigma}}{\gamma_{&space;_Mf}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_{d,\\Delta\\sigma}=\\frac{R_{d,\\Delta\\sigma}}{\\gamma_{&space;_Mf}

              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_{d,\\Delta\\sigma}=\\frac{R_{d,\\Delta\\sigma}}{\\gamma_{&space;_{Mf}}}",
                height: 15,
                width: 64,
              },
            }}
          />

          {/* Ausnutzung */}
          <DataRow
            item={{
              title: t("inner.fatigue.utilisationFactor"),
              value: data.Germany.Innere.Ermuedung.ausnutzung,

              // \eta
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}\eta
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\eta
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\eta",
                height: 6,
                width: 4,
              },
              decimalSeparator: decimalSeparator(),
              thousandSeparator: thousandSeparator(),
              valueScale: 2,
              itemIndex: itemIndex++,
              // \eta=\frac{F_{d,\Delta\sigma}}{R_{d,\Delta\sigma}}\leq 1,0
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}\eta=\frac{F_{d,\Delta\sigma}}{R_{d,\Delta\sigma}}\leq&space;1,0
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\eta=\\frac{F_{d,\\Delta\\sigma}}{R_{d,\\Delta\\sigma}}\\leq&space;1,0

              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\eta=\\frac{F_{d,\\Delta\\sigma}}{R_{d,\\Delta\\sigma}}\\leq&space;1,0",
                height: 14,
                width: 58,
              },
            }}
          />
          <InvoiceTableBlankSpace rowsCount={25 - itemIndex} />
        </View>
      </View>
    </PageWrapper>
  );
}
